import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getFirestore,
  collection,
  getDocs,
  query,
  orderBy,
} from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";

const Haberler = () => {
  const [newsList, setNewsList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAllNews = async () => {
      try {
        const db = getFirestore();
        const storage = getStorage();

        const newsRef = collection(db, "news");
        const newsQuery = query(newsRef, orderBy("timestamp", "desc"));
        const snapshot = await getDocs(newsQuery);

        if (snapshot.empty) {
          setNewsList([]);
          return;
        }

        const allNews = await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            let imageUrl = "/images/placeholder.jpg";

            if (data.image_url) {
              try {
                const imageRef = ref(storage, data.image_url);
                imageUrl = await getDownloadURL(imageRef);
              } catch (err) {
                console.warn("Görsel yüklenemedi:", err.message);
              }
            }

            let formattedDate = "Tarih belirtilmemiş";
            if (data.timestamp) {
              try {
                formattedDate = data.timestamp
                  .toDate()
                  .toLocaleDateString("tr-TR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  });
              } catch {}
            }

            return {
              id: doc.id,
              title: data.title || "Başlıksız Haber",
              content: data.content || "İçerik bulunamadı.",
              date: formattedDate,
              imageUrl,
              categories: data.categories || [],
            };
          })
        );

        setNewsList(allNews);
      } catch (err) {
        console.error(err);
        setError("Haberler yüklenirken bir hata oluştu.");
      }
    };

    const fetchCategories = async () => {
      try {
        const db = getFirestore();
        const categoryRef = collection(db, "news_categories");
        const snapshot = await getDocs(categoryRef);

        const categoryList = snapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().display_name || doc.id,
        }));

        setCategories(categoryList);
      } catch (err) {
        console.error("Kategoriler yüklenemedi:", err);
      }
    };

    fetchAllNews();
    fetchCategories();
  }, []);

  const handleCardClick = (id) => {
    navigate(`/haber/${id}`);
  };

  const filteredNews =
    selectedCategory === "all"
      ? newsList
      : newsList.filter(
          (news) => news.categories?.[0] === selectedCategory
        );

  if (error) {
    return (
      <div className="news-error">
        <p>⚠️ {error}</p>
      </div>
    );
  }

  if (newsList.length === 0) {
    return (
      <div className="news-empty">
        <p>Henüz haber eklenmemiş.</p>
      </div>
    );
  }

  return (
    <div className="news-section">
      <div className="news-filters">
        <button
          className={selectedCategory === "all" ? "active" : ""}
          onClick={() => setSelectedCategory("all")}
        >
          Tümü
        </button>

        {categories.map((cat) => (
          <button
            key={cat.id}
            className={selectedCategory === cat.id ? "active" : ""}
            onClick={() => setSelectedCategory(cat.id)}
          >
            {cat.name}
          </button>
        ))}
      </div>

      <div className="news-grid">
        {filteredNews.map((item) => (
          <div
            key={item.id}
            className="news-card"
            onClick={() => handleCardClick(item.id)}
          >
            <div className="news-image-container">
              <img
                src={item.imageUrl}
                alt={item.title}
                className="news-image"
                onError={(e) => {
                  e.currentTarget.src = "/images/placeholder.jpg";
                }}
              />
            </div>

            <div className="news-content">
              <h3 className="news-title">{item.title}</h3>

              <div className="news-description">
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                  {item.content.length > 130
                    ? item.content.slice(0, 130) + "..."
                    : item.content}
                </ReactMarkdown>
              </div>

              <div className="news-meta">
                <span>Tarih: {item.date}</span>
              </div>

              <button
                className="news-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCardClick(item.id);
                }}
              >
                Haberi Oku
              </button>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .news-section { padding: 70px 20px; background-color: #f9fafc; font-family: "Inter", sans-serif; }

        .news-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: center;
          margin-bottom: 40px;
        }

        .news-filters button {
          padding: 10px 16px;
          border-radius: 20px;
          border: 1px solid #ddd;
          background: #fff;
          cursor: pointer;
          font-size: 0.9rem;
          transition: all 0.3s ease;
        }

        .news-filters button:hover {
          background: #f0f0f0;
        }

        .news-filters button.active {
          background: #ffc107;
          border-color: #ffc107;
          font-weight: 600;
        }

        /* 🔥 SABİT GRID */
        .news-grid {
          display: grid;
          grid-template-columns: repeat(4, 300px);
          gap: 25px;
          justify-content: center;
          max-width: 1300px;
          margin: 0 auto;
        }

        /* responsive */
        @media (max-width: 1200px) {
          .news-grid {
            grid-template-columns: repeat(3, 300px);
          }
        }

        @media (max-width: 900px) {
          .news-grid {
            grid-template-columns: repeat(2, 300px);
          }
        }

        @media (max-width: 600px) {
          .news-grid {
            grid-template-columns: repeat(1, 300px);
          }
        }

        .news-card {
          width: 300px;
          background-color: #fff;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          cursor: pointer;
          display: flex;
          flex-direction: column;
        }

        .news-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 18px rgba(0,0,0,0.15);
        }

        .news-image-container { height: 200px; background: #f5f5f5; }
        .news-image { width: 100%; height: 100%; object-fit: cover; }

        .news-content {
          padding: 18px 20px 24px;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .news-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 10px;
          color: #333;
        }

        .news-description {
          font-size: 0.9rem;
          color: #666;
          line-height: 1.5;
          margin-bottom: 15px;
          flex-grow: 1;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .news-meta {
          font-size: 0.85rem;
          color: #777;
          margin-bottom: 12px;
        }

        .news-button {
          padding: 12px;
          background-color: #ffc107;
          color: #333;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.3s ease;
        }

        .news-button:hover {
          background-color: #e0a800;
        }

        .news-error, .news-empty {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 80px 20px;
          font-size: 1rem;
          color: #555;
        }
      `}</style>
    </div>
  );
};

export default Haberler;